import { Alert, Flex } from 'antd';
import { push } from 'connected-react-router';
import { Inventory } from 'lib/documents/inventory';
import { useQuestionnaireProgression } from 'lib/hooks/use-questionnaire-progression';
import { useReducer } from 'lib/hooks/useReducer';
import lodash from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PatientRoutes } from 'routes';

import { CRX_SUPPORT_EMAIL, IS_DEVELOPMENT } from 'app/config';
import { getPatientLinkUrl } from 'navigation/router/route-switch/route-switch-patient/utils/patient-route-helpers';
import { useParams } from 'react-router-dom';
import {
  calculateSubmitReady,
  isDisabled,
  nextPageOrSection,
  paginationSlice,
  paginationSliceInitialState,
  PaginationSliceReducer,
  setSections
} from '../questionnaires/paginationSlice';
import { DisplaySection } from '../assessment-controller/_components/display-section';
import { QAPanel } from '../assessment-controller/_components/questionnaire-qa-panel';
import {
  cleanPagedQuestionnaireStorage,
  saveStateToStorage,
  tryRestoreStateFromStorage
} from '../_utils/questionnaire-local-storage';
import { QuestionConfigWithResponse } from './questionnaire-page';
import {
  getCurrentProgress,
  useInitialiseExtraHooks
} from '../_utils/useInitialiseExtraHooks';
import {
  BaseReducer,
  PagedQuestionnaireProps,
  QuestionnairePaginationCombinedGeneralProps
} from './questionnaire-paginator-types';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';

export function PagedQuestionnaireWrapper<
  A extends BaseReducer,
  T extends Inventory
>(
  props: PagedQuestionnaireProps<A, T> &
    Pick<
      QuestionnairePaginationCombinedGeneralProps<T>,
      | 'pageTitle'
      | 'onPageChange'
      | 'onSectionChange'
      | 'onSubmitExtraNavigationProps'
    >
) {
  const { patientId } = useParams<{ patientId: string }>();
  const [state, dataDispatch] = useReducer(
    props.reducer,
    tryRestoreStateFromStorage(props.storageKey, props.initialState)
  );

  const paginationInitialState = useMemo(
    () =>
      tryRestoreStateFromStorage(
        `${props.storageKey}-pagination`,
        paginationSliceInitialState
      ),
    // Only run this once
    // eslint-disable-next-line
    []
  );

  const data = useMemo(
    () => props.getData(state),
    // eslint-disable-next-line
    [props.getData, state]
  );

  return (
    <PagedQuestionnaireGeneral
      invalidItems={state.invalidItems}
      paginationInitialState={paginationInitialState}
      data={data}
      formatData={props.actions.formatData}
      submit={postSubmit => {
        dataDispatch(
          props.actions.submit({
            ...postSubmit,
            onSuccess: () => {
              // Remove the cached questionnaire progress
              cleanPagedQuestionnaireStorage(props.storageKey);

              // Continue the callback chain
              postSubmit.onSuccess();
            }
          })
        );
      }}
      updateItem={item => dataDispatch(props.actions.updateItem(item))}
      validate={() => dataDispatch(props.actions.validate())}
      pageTitle={props.pageTitle}
      onPageChange={props.onPageChange}
      onSectionChange={props.onSectionChange}
      onStateChange={paginationState => {
        saveStateToStorage(props.storageKey, state);
        saveStateToStorage(`${props.storageKey}-pagination`, paginationState);
      }}
      onSubmitExtraNavigationProps={props.onSubmitExtraNavigationProps}
      patientId={patientId}
    />
  );
}

function useSubmitThroughDispatchMutation(
  submitCallback: QuestionnairePaginationCombinedGeneralProps<any>['submit'],
  extraOptions?: Omit<UseMutationOptions, 'mutationFn'>
) {
  return useMutation({
    ...extraOptions,
    mutationFn: () =>
      /**
       * Pass custom success and error handlers into the event
       * This is a temporary solution to get access to the submission execution context
       *
       * TODO: Remove this when questionnaire submissions are refactored to not be invoked through Redux
       */
      new Promise<void>((res, rej) => {
        submitCallback({
          onSuccess: () => res(),
          onError: error => rej(error)
        });
      })
  });
}

function PagedQuestionnaireGeneral<T extends Inventory>(
  props: QuestionnairePaginationCombinedGeneralProps<T>
) {
  const reduxDispatch = useDispatch();
  const [paginationState, paginationDispatch] = useReducer(
    PaginationSliceReducer,
    props.paginationInitialState
  );
  const submissionMutaion = useSubmitThroughDispatchMutation(props.submit, {
    onSuccess: () => {
      // Navigate to the assessment control flow when the submission is successful
      reduxDispatch(
        push(
          getPatientLinkUrl(
            PatientRoutes.AssessmentControlFlow,
            props.patientId
          ),
          {
            fromQuestionnaire: true,
            ...props.onSubmitExtraNavigationProps
          }
        )
      );
    }
  });

  const curProgression = getCurrentProgress(paginationState);
  const maxProgression = paginationState.formattedSections.length;

  useQuestionnaireProgression(curProgression, maxProgression);
  useInitialiseExtraHooks(paginationState, props);

  /**
   * Process the input data and save it into `paginationSlice.formattedSections`
   */
  useEffect(() => {
    const _data = lodash.cloneDeep(props.data);
    const resolvedFormattedSections = props.formatData(_data, 4);

    paginationDispatch(setSections(resolvedFormattedSections));
    paginationDispatch(isDisabled());
    props.validate();
    // eslint is disabled so that props doesn't need to be destructured
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  function handleOnValueChange(
    item: Pick<QuestionConfigWithResponse, 'value' | 'title'>
  ) {
    props.updateItem(item);
    paginationDispatch(isDisabled());
    paginationDispatch(paginationSlice.actions.calculateSubmitReady());
    props.validate();
  }

  const submitReady = calculateSubmitReady(paginationState);

  async function handleNextOrSubmit() {
    /**
     * If the page is ready for submission
     */
    if (submitReady) {
      submissionMutaion.mutate();
      return;
    }

    /**
     * Otherwise continue to the next page or section
     */
    paginationDispatch(nextPageOrSection());
    paginationDispatch(isDisabled());
  }

  const currentSection = paginationState.formattedSections.at(
    paginationState.currentSection
  );

  return (
    <div>
      {IS_DEVELOPMENT && (
        <Flex justify="center">
          <QAPanel
            paginationState={paginationState}
            handleOnValueChange={handleOnValueChange}
          />
        </Flex>
      )}

      {submissionMutaion.isError && (
        <ErrorDisplay
          title={`Inventory failed to save`}
          description={`Please try again. If the error persists please contact us at ${CRX_SUPPORT_EMAIL}`}
          error={resolveError(submissionMutaion.error)}
        />
      )}

      {currentSection ? (
        <DisplaySection
          currentPage={paginationState.currentPage}
          currentSection={currentSection}
          currentSectionIndex={paginationState.currentSection}
          handleOnValueChange={handleOnValueChange}
          handleNextOrSubmit={handleNextOrSubmit}
          pageTitle={props.pageTitle}
          submitReady={submitReady}
          isSubmitting={submissionMutaion.isLoading}
        />
      ) : (
        <Alert type="warning" message="No sections were loaded" />
      )}
    </div>
  );
}
